import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, CircleNotch, XCircle, ArrowLeft } from "@phosphor-icons/react";
import { getPaymentStatus } from "@/lib/api";
import { useCart } from "@/lib/cart";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [order, setOrder] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let polls = 0;
    let timer;
    const poll = async () => {
      try {
        const data = await getPaymentStatus(sessionId);
        if (data.payment_status === "paid") {
          setOrder(data);
          setState("paid");
          if (!cleared.current) { clear(); cleared.current = true; }
          return;
        }
        if (data.status === "failed") { setState("error"); return; }
        if (polls >= MAX_POLLS) { setState("pending"); return; }
        polls += 1;
        timer = setTimeout(poll, 2000);
      } catch {
        if (polls >= MAX_POLLS) { setState("error"); return; }
        polls += 1;
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, clear]);

  return (
    <div className="min-h-[70vh] grid place-items-center pt-32 px-5 text-center" data-testid="payment-success-page">
      <div className="max-w-md">
        {state === "checking" && (
          <div data-testid="payment-checking">
            <CircleNotch size={52} className="animate-spin text-navy-600 mx-auto" />
            <h1 className="font-display font-black text-2xl text-navy-900 mt-5">Confirming your payment…</h1>
            <p className="text-slate-500 mt-2">Hang tight, this only takes a moment.</p>
          </div>
        )}
        {state === "paid" && (
          <div data-testid="payment-paid">
            <CheckCircle size={60} weight="fill" className="text-green-500 mx-auto" />
            <h1 className="font-display font-black text-3xl text-navy-900 mt-4">Payment complete!</h1>
            <p className="text-slate-600 mt-3">Thank you for your order{order?.amount ? <> — <strong>${Number(order.amount).toFixed(2)}</strong></> : ""}. We've received it and a receipt is on its way. We'll be in touch about fulfillment.</p>
            <Link to="/supply" className="inline-flex items-center gap-2 mt-7 rounded-full bg-navy-900 text-white px-6 py-3 font-bold hover:bg-navy-600 transition-colors"><ArrowLeft size={16} /> Back to AMWAP Supply</Link>
          </div>
        )}
        {state === "pending" && (
          <div data-testid="payment-pending">
            <CircleNotch size={52} className="text-amber-500 mx-auto" />
            <h1 className="font-display font-black text-2xl text-navy-900 mt-5">Payment is processing.</h1>
            <p className="text-slate-500 mt-2">Your payment is still being confirmed. You'll get a receipt by email once it clears.</p>
            <Link to="/supply" className="inline-flex items-center gap-2 mt-7 rounded-full bg-navy-900 text-white px-6 py-3 font-bold hover:bg-navy-600 transition-colors">Back to store</Link>
          </div>
        )}
        {state === "error" && (
          <div data-testid="payment-error">
            <XCircle size={56} weight="fill" className="text-red-500 mx-auto" />
            <h1 className="font-display font-black text-2xl text-navy-900 mt-4">We couldn't confirm this payment.</h1>
            <p className="text-slate-500 mt-2">If you were charged, don't worry — reach out and we'll sort it out right away.</p>
            <Link to="/supply/cart" className="inline-flex items-center gap-2 mt-7 rounded-full bg-navy-900 text-white px-6 py-3 font-bold hover:bg-navy-600 transition-colors">Back to cart</Link>
          </div>
        )}
      </div>
    </div>
  );
}
